import {Input, Select, Spin} from 'antd'
import classNames from 'classnames'
import FieldDecorator from 'components/form/FieldDecorator'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import Pure from 'components/Pure'
import {Formik, FormikConsumer} from 'formik'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo} from 'react'
import {CodiContentTypes} from 'views/AppLocals/Codi/codiHelpers'
import LoadingPage from 'views/LoadingPage'
import {NullAsDefault} from 'views/Shared'
import {getInnerContentTypes} from './codiHelpers'
import {SelectWidget} from '../custom/ReactSelectComponents'
import {createControlledFormField} from '../factory/createFormField'
import RowSettingContext, {SettingRowHeader, useCodiRowSettings,} from './RowSettingContext'

const content_types = [
  {
    name: CodiContentTypes.SUPER_PART,
    label: 'part-cd',
    fieldNames: ['sup_part_cd', 'sup_part_content'],
  },
  {
    name: CodiContentTypes.PART,
    label: 'part-cd',
    fieldNames: ['part_cd', 'part_content'],
  },
  {
    name: CodiContentTypes.APPENDIX,
    label: 'appendix-cd',
    fieldNames: ['appendix_cd', 'appendix_content'],
  },
  {
    name: CodiContentTypes.CHAPTER,
    label: 'chapter-cd',
    fieldNames: ['chapter_cd', 'chapter_content'],
  },
  {
    name: CodiContentTypes.SUB_CHAPTER,
    label: 'chapter-cd',
    fieldNames: ['sub_chapter_cd', 'sub_chapter_content'],
  },
  {
    name: CodiContentTypes.SUPER_CHAPTER,
    label: 'chapter-cd',
    fieldNames: ['sup_chapter_cd', 'sup_chapter_content'],
  },
  {
    name: CodiContentTypes.SECTION,
    label: 'section-cd',
    fieldNames: ['section_cd', 'section_content'],
  },
  {
    name: CodiContentTypes.SUB_SECTION,
    label: 'sub_section_cd',
    fieldNames: ['sub_section_cd', 'sub_section_content'],
  },
  {
    name: CodiContentTypes.SUPER_CHAPTER,
    label: 'sup_section_cd',
    fieldNames: ['sup_section_cd', 'sup_section_content'],
  },
  {
    name: CodiContentTypes.ARTICLE,
    label: 'article-cd',
    fieldNames: ['article_cd', 'article_content'],
  },
  {
    name: CodiContentTypes.CLAUSE,
    fieldNames: ['clause_cd', 'clause_content'],
    label: 'clause-cd',
    renderAddonBefore: () => (
      <Translate>
        {(t) => (
          <span className="capitalize text-xs font-medium">{t('clause')}</span>
        )}
      </Translate>
    ),
  },
  {
    name: CodiContentTypes.POINT,
    fieldNames: ['point_cd', 'point_content'],
    label: 'point-cd',
    renderAddonBefore: () => (
      <Translate>
        {(t) => (
          <span className="capitalize text-xs font-medium">{t('point')}</span>
        )}
      </Translate>
    ),
  },
  {
    name: CodiContentTypes.PARAGRAPH,
    label: 'paragraph-cd',
    fieldNames: ['paragraph_cd', 'paragraph_content'],
  },
  {
    name: CodiContentTypes.SENTENCE,
    label: 'sentence-cd',
    fieldNames: ['sentence_cd', 'sentence_content'],
  },
]

const formSchema = {
  content: {
    title: 'content',
    children: [
      {
        name: 'content',
        component: createControlledFormField({
          rows: 5,
          placeholder: 'content',
          Component: Input.TextArea,
        }),
      },
    ],
  },
  is_html: {
    title: 'content type',
    children: [
      {
        name: 'is_html',
        component: ({ name, value, onChange }) => (
          <Translate>
            {(t) => (
              <SelectWidget
                value={value}
                className="w-full"
                placeholder={t('content type')}
                onChange={(value) => {
                  onChange(createValue(name, value))
                }}>
                <Select.Option value={false}>{t('text')}</Select.Option>
                <Select.Option value={true}>{t('html')}</Select.Option>
              </SelectWidget>
            )}
          </Translate>
        ),
      },
    ],
  },
  index_typecd: {
    title: 'type-cd',
    children: [
      {
        name: 'index_typecd',
        component: ({ name, form, value, onChange }) => {
          const { values, setFieldValue = NullAsDefault } = form ?? {}
          const options = Array.from(values.index_typecds ?? [])
          return (
            <SelectWidget
              value={value}
              className="w-full"
              allowClear={true}
              options={options}
              onChange={(value) => {
                setFieldValue('is_html', false)
                setFieldValue('index_cd', undefined)
                onChange(createValue(name, value ?? null))
              }}
              placeholder={
                <Translate>
                  {(t) => (
                    <span className="text-xs italic text-color-400">
                      {t('ex:type-cd')}
                    </span>
                  )}
                </Translate>
              }
            />
          )
        },
      },
    ],
  },
}

const IndexCDField = ({ name, ...props }) => {
  const t = useTranslate()
  return (
    <FormikConsumer>
      {({ errors, values, handleChange }) => {
        const { index_cd, index_typecd } = values ?? {}
        const is_content = index_typecd === CodiContentTypes.CONTENT
        const { label, renderAddonBefore } =
          _.find(content_types, { name: index_typecd }) ?? {}
        if (is_content) {
          return (
            <Pure>
              <FieldsFactory formSchema={[formSchema.is_html]} />
            </Pure>
          )
        } else if (index_typecd) {
          return (
            <FieldDecorator
              label={t(index_typecd)}
              errors={errors.index_cd}
              labelProps={{ className: undefined }}>
              <Input
                addonBefore={renderAddonBefore}
                placeholder={label ? t(label) : undefined}
                {...props}
                name={name}
                value={index_cd}
                onChange={handleChange}
              />
            </FieldDecorator>
          )
        } else {
          return null
        }
      }}
    </FormikConsumer>
  )
}

export const InsertContent = ({
  style,
  className,
  onCancel = NullAsDefault,
  onSuccess = NullAsDefault,
}) => {
  const t = useTranslate()

  const {
    item,
    row_id,
    codi_id,
    isLoading = false,
  } = useContext(RowSettingContext)

  const { onCreate = NullAsDefault, asyncCreating } = useCodiRowSettings({
    row_id,
    codi_id,
    notifyListeners: {
      onCreated: (result, data) => {
        onSuccess(result, data, row_id)
      },
    },
  })

  const isSubmitting = Boolean(asyncCreating?.isLoading)

  const initialValues = useMemo(() => {
    const current_typecd = item?.index_typecd
    return {
      is_html: false,
      current_typecd,
      index_typecds: getInnerContentTypes(current_typecd).map((name) => ({
        label: t(name),
        value: name,
      })),
    }
  }, [t, item])

  const validate = (values) => {
    let errors = {}
    _.forEach(['index_typecd'], (name) => {
      const value = _.get(values, name)
      if (_.isEmpty(value ?? undefined)) {
        errors[name] = t('required field')
      }
    })
    return errors
  }

  return (
    <div
      style={style}
      className={classNames(
        'flex flex-col flex-1 px-8 custom-form-codi',
        className
      )}>
      <div className="flex justify-center font-bold text-color-000 text-xl uppercase">
        {t('Embed a section of a document')}
      </div>
      {isLoading ? (
        <LoadingPage />
      ) : (
        <div className="flex flex-col flex-1">
          <SettingRowHeader />
          <Formik
            validate={validate}
            onSubmit={(values, { setSubmitting, setFieldValue }) => {
              const {
                content,
                index_cd,
                index_typecd,
                index_typecds,
                current_typecd,
                ...rest
              } = values ?? {}
              setSubmitting(true)
              onCreate(
                _.omitBy(
                  {
                    content,
                    index_cd,
                    index_typecd,
                    is_html: Number(values?.is_html ?? 0),
                  },
                  _.isUndefined
                )
              )
            }}
            enableReinitialize={true}
            initialValues={initialValues}>
            <React.Fragment>
              <Spin spinning={isSubmitting}>
                <div className="flex flex-col space-y-2">
                  <Pure>
                    <FieldsFactory formSchema={[formSchema.index_typecd]} />
                  </Pure>
                  <IndexCDField name="index_cd" />
                  <Pure>
                    <FieldsFactory formSchema={[formSchema.content]} />
                  </Pure>
                </div>
              </Spin>
              <div className="flex-1" />
              <FormActionBar
                className="background"
                cancelSubmit={asyncCreating?.error}
              />
            </React.Fragment>
          </Formik>
        </div>
      )}
    </div>
  )
}

export const EditContent = ({
  style,
  className,
  onCancel = NullAsDefault,
  onSuccess = NullAsDefault,
}) => {
  const t = useTranslate()

  const {
    item,
    row_id,
    codi_id,
    isLoading = false,
  } = useContext(RowSettingContext)

  const { asyncUpdating, onUpdate = NullAsDefault } = useCodiRowSettings({
    row_id,
    codi_id,
    notifyListeners: {
      onUpdated: (result, data) => {
        onSuccess(result, data, row_id)
      },
    },
  })

  const isSubmitting = Boolean(asyncUpdating?.isLoading)

  const initialValues = useMemo(() => {
    const { content, index_typecd } = item ?? {}
    const { fieldNames = [,] } =
      _.find(content_types, { name: index_typecd }) ?? {}
    return {
      ...(item ?? {}),
      content:
        index_typecd === CodiContentTypes.CONTENT
          ? content
          : _.get(item, fieldNames[1]),
      index_cd: _.get(item, fieldNames[0]),
    }
  }, [t, item])

  const validate = (values) => {
    let errors = {}
    _.forEach(['index_typecd'], (name) => {
      const value = _.get(values, name)
      if (_.isEmpty(value ?? undefined)) {
        errors[name] = t('required field')
      }
    })
    return errors
  }

  return (
    <div
      style={style}
      className={classNames(
        'flex flex-col flex-1 px-8 custom-form-codi',
        className
      )}>
      <div className="flex justify-center font-bold text-color-000 text-xl uppercase">
        {t('edit content')}
      </div>
      {isLoading ? (
        <LoadingPage />
      ) : (
        <div className="flex flex-col flex-1">
          <SettingRowHeader />
          <Formik
            validate={validate}
            onSubmit={(values, { setSubmitting }) => {
              const { content, index_cd, is_html } = values ?? {}
              setSubmitting(true)
              onUpdate({
                content,
                index_cd,
                is_html: Number(is_html ?? 0),
              })
            }}
            enableReinitialize={true}
            initialValues={initialValues}>
            <React.Fragment>
              <Spin spinning={isSubmitting}>
                <div className="flex flex-col space-y-2">
                  <IndexCDField name="index_cd" />
                  <Pure>
                    <FieldsFactory
                      formSchema={[formSchema.is_html, formSchema.content]}
                    />
                  </Pure>
                </div>
              </Spin>
              <div className="flex-1" />
              <FormActionBar
                className="background"
                cancelSubmit={asyncUpdating?.error}
              />
            </React.Fragment>
          </Formik>
        </div>
      )}
    </div>
  )
}
